import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, Shield, Award, FileCheck, Leaf, AlertTriangle, GraduationCap, Heart } from 'lucide-react';

const SafetyPage = () => {
  const certifications = [
    { icon: Award, title: 'ISO 9001:2015', description: 'Kalite Yönetim Sistemi Sertifikası', detail: 'Uluslararası kalite standartlarına uygunluk' },
    { icon: Shield, title: 'ADR Belgesi', description: 'Tehlikeli Madde Taşımacılığı Yetki Belgesi', detail: 'Tüm ADR sınıfları (1-9) için geçerli' },
    { icon: FileCheck, title: 'C2 Yetki Belgesi', description: 'Uluslararası Eşya Taşımacılığı', detail: 'T.C. Ulaştırma Bakanlığı onaylı' },
    { icon: FileCheck, title: 'K1 Yetki Belgesi', description: 'Yurtiçi Eşya Taşımacılığı', detail: 'T.C. Ulaştırma Bakanlığı onaylı' },
    { icon: Leaf, title: 'ISO 14001', description: 'Çevre Yönetim Sistemi', detail: 'Çevreye duyarlı operasyon taahhüdü' },
  ];

  const safetyProtocols = [
    {
      title: 'Yükleme Öncesi Kontrol',
      items: ['Araç teknik durum kontrolü', 'Tank ve valf sistem kontrolü', 'Topraklama sistemi testi', 'Yangın söndürme ekipmanı kontrolü', 'ADR ekipman kontrolü'],
    },
    {
      title: 'Taşıma Sürecinde',
      items: ['Hız limitlerine tam uyum', 'Zorunlu mola ve dinlenme süreleri', 'Yasak bölge ve güzergah takibi', 'Anlık konum ve durum izleme', 'Acil durum iletişim protokolü'],
    },
    {
      title: 'Boşaltma Prosedürü',
      items: ['Teslimat noktası güvenlik kontrolü', 'Topraklama bağlantısı', 'Kontrollü boşaltma süreci', 'Miktar ve kalite doğrulama', 'Dokümantasyon tamamlama'],
    },
  ];

  const trainingPrograms = [
    { icon: GraduationCap, title: 'ADR Sürücü Eğitimi', description: 'Tehlikeli madde taşımacılığı için zorunlu sürücü sertifikasyon programı.', duration: '40 Saat' },
    { icon: Shield, title: 'Güvenli Sürüş Eğitimi', description: 'Defensif sürüş teknikleri ve acil durum manevraları.', duration: '16 Saat' },
    { icon: AlertTriangle, title: 'Acil Durum Müdahale', description: 'Kaza, yangın ve sızıntı durumlarında müdahale prosedürleri.', duration: '8 Saat' },
    { icon: Heart, title: 'İlk Yardım Eğitimi', description: 'Temel ilk yardım ve yaşam kurtarma teknikleri.', duration: '16 Saat' },
  ];

  const faqItems = [
    { question: 'ADR belgesi nedir ve neden önemlidir?', answer: 'ADR (Tehlikeli Malların Karayoluyla Uluslararası Taşınmasına İlişkin Avrupa Anlaşması), tehlikeli maddelerin güvenli taşınması için belirlenen uluslararası standartlardır.' },
    { question: 'Araçlarınız ne sıklıkla bakıma alınıyor?', answer: 'Tüm araçlarımız aylık periyodik bakım programına dahildir. Haftalık görsel kontroller, her taşıma öncesi teknik kontroller ve yıllık kapsamlı muayeneler yapılmaktadır.' },
    { question: 'Acil durumlarda nasıl müdahale ediyorsunuz?', answer: '7/24 aktif acil durum hattımız ve müdahale ekiplerimiz mevcuttur. Ortalama müdahale süremiz 2 saatin altındadır.' },
    { question: 'Çevre güvenliği konusunda ne gibi önlemler alıyorsunuz?', answer: 'ISO 14001 çevre yönetim sistemine sahibiz. Araçlarımızda sızıntı önleme sistemleri, döküntü toplama ekipmanları bulunur.' },
  ];

  return (
    <div data-testid="safety-page" className="bg-white">
      <section className="page-hero-banner">
        <img src="/images/safety-truck.webp" alt="" className="hero-bg" loading="eager" />
        <div className="hero-gradient" />
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Güvenlik & Kalite</span>
            <h1 className="heading-xl text-white mb-4">Güvenlik. Kontrol. Denetim.</h1>
            <p className="text-base text-white/70 leading-relaxed max-w-lg">
              Tehlikeli madde taşımacılığında en yüksek standartlar.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Sertifikalar</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Uluslararası standartlarda yetkinlik.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div key={cert.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-8 text-center hover:border-[#0B3A82]/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0B3A82]/8 flex items-center justify-center mb-5">
                  <cert.icon className="w-7 h-7 text-[#0B3A82]" />
                </div>
                <h3 className="text-base font-bold text-[#1A1A2E] mb-2">{cert.title}</h3>
                <p className="text-sm text-[#64748B] mb-2">{cert.description}</p>
                <p className="text-xs text-[#FF6B2C] font-semibold">{cert.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Güvenlik Protokolleri</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Her aşamada kontrol.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {safetyProtocols.map((protocol, index) => (
              <motion.div key={protocol.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden"
              >
                <div className="bg-[#0B3A82] p-6">
                  <h3 className="text-lg font-bold text-white">{protocol.title}</h3>
                </div>
                <div className="p-7">
                  <ul className="space-y-3.5">
                    {protocol.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-[#FF6B2C] rounded-full flex-shrink-0" />
                        <span className="text-sm text-[#64748B]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Eğitim Programları</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Eğitimli kadro, güvenli operasyon.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingPrograms.map((program, index) => (
              <motion.div key={program.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-white border border-[#E2E8F0] rounded-2xl p-8 hover:shadow-lg hover:border-[#0B3A82]/20 transition-all duration-300"
              >
                <div className="icon-box mb-5"><program.icon className="w-5 h-5" /></div>
                <h3 className="text-base font-bold text-[#1A1A2E] mb-2">{program.title}</h3>
                <p className="text-sm text-[#64748B] mb-4 leading-relaxed">{program.description}</p>
                <div className="inline-block bg-[#0B3A82]/8 text-[#0B3A82] text-xs font-bold px-3 py-1.5 rounded-full">{program.duration}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Sık Sorulan Sorular</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Merak edilenler.</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}
                className="bg-white border border-[#E2E8F0] rounded-xl px-6 data-[state=open]:border-[#0B3A82]/20 data-[state=open]:shadow-md transition-all"
              >
                <AccordionTrigger className="text-left text-[#1A1A2E] font-semibold hover:text-[#0B3A82] hover:no-underline py-5 text-[0.9375rem]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#64748B] leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="cta-section section-padding-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="heading-lg text-white mb-5">Güvenli operasyon için ekibimizle görüşün.</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-10 text-lg">Sertifikalı filo ve deneyimli kadromuzla yanınızdayız.</p>
            <Link to="/iletisim">
              <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-10 py-6 text-base shadow-lg group" data-testid="safety-cta-button">
                Ekibimizle görüşün
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SafetyPage;
